import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    ShieldAlert, Plus, Search, ArrowRight, MessageSquare, Paperclip,
    UserCheck, X as XIcon,
} from "lucide-react";
import { STATUS_META, SEVERITY_META, fmtDt, statusLabel, severityLabel } from "../lib/incidents";
import { t } from "../lib/i18n";

/** Central list of Incidents / skadesaker.
 *  Admins see every case + create button. Customers see only their own. */
export default function IncidentsPage() {
    const { user } = useAuth();
    const nav = useNavigate();
    const isAdmin = user?.role === "admin";
    const lang = !isAdmin && user?.language === "en" ? "en" : "no";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [openCreate, setOpenCreate] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/incidents");
            setItems(data || []);
        } catch {
            toast.error(lang === "en" ? "Could not load cases" : "Kunne ikke laste saker");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return items.filter((it) => {
            if (statusFilter !== "all" && it.status !== statusFilter) return false;
            if (!query) return true;
            const q = query.toLowerCase();
            return (
                (it.reference_no || "").toLowerCase().includes(q) ||
                (it.title || "").toLowerCase().includes(q) ||
                (it.customer_name || "").toLowerCase().includes(q)
            );
        });
    }, [items, query, statusFilter]);

    return (
        <div className="max-w-5xl mx-auto space-y-5 pb-16" data-testid="incidents-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] flex items-center gap-2">
                        <ShieldAlert className="w-7 h-7 text-red-600" />
                        {isAdmin ? "Hendelser & skadesaker" : t(lang, "incidents_title_customer")}
                    </h1>
                    <p className="text-sm text-[#2B3F4D] mt-1">
                        {isAdmin
                            ? "Oversikt over alle registrerte ulykker, kollisjoner og skader."
                            : t(lang, "incidents_subtitle_customer")}
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={() => setOpenCreate(true)}
                        className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                        data-testid="incidents-create-open"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Ny sak
                    </Button>
                )}
            </div>

            {items.length > 0 && (
                <Card className="p-3 flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={isAdmin
                                ? "Søk på ref, tittel eller kunde…"
                                : t(lang, "incidents_search_placeholder")}
                            className="pl-8"
                            data-testid="incidents-search"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[200px]" data-testid="incidents-status-filter">
                            <SelectValue placeholder={t(lang, "incidents_status_filter_all")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t(lang, "incidents_status_filter_all")}</SelectItem>
                            {Object.keys(STATUS_META).map((k) => (
                                <SelectItem key={k} value={k}>{statusLabel(k, lang)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Card>
            )}

            {loading ? (
                <div className="text-center text-[#2B3F4D] py-8">{t(lang, "incidents_loading")}</div>
            ) : filtered.length === 0 ? (
                <Card className="p-8 text-center text-[#2B3F4D]" data-testid="incidents-empty">
                    <ShieldAlert className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <div className="font-semibold text-[#0C1824]">{t(lang, "incidents_empty_title")}</div>
                    <div className="text-sm mt-1">
                        {isAdmin
                            ? "Trykk «Ny sak» for å registrere en hendelse."
                            : t(lang, "incidents_empty_body_customer")}
                    </div>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filtered.map((it) => (
                        <IncidentRow
                            key={it.id}
                            incident={it}
                            lang={lang}
                            isAdmin={isAdmin}
                            onOpen={() => nav(`/incidents/${it.id}`)}
                        />
                    ))}
                </div>
            )}

            {isAdmin && (
                <CreateIncidentDialog
                    open={openCreate}
                    onOpenChange={setOpenCreate}
                    onCreated={() => { setOpenCreate(false); load(); }}
                />
            )}
        </div>
    );
}

function IncidentRow({ incident, lang = "no", isAdmin, onOpen }) {
    const st = STATUS_META[incident.status] || STATUS_META.open;
    const sev = SEVERITY_META[incident.severity] || SEVERITY_META.moderate;
    return (
        <Card
            className="p-4 hover:shadow-md transition cursor-pointer"
            onClick={onOpen}
            data-testid={`incident-row-${incident.id}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                        <span className="font-mono text-[#A57F39] font-semibold">
                            {incident.reference_no}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full border ${st.cls}`}>
                            {statusLabel(incident.status, lang)}
                        </span>
                        <span className={sev.cls}>· {severityLabel(incident.severity, lang)}</span>
                    </div>
                    <div className="mt-1 font-semibold text-[#0C1824] text-lg">
                        {incident.title}
                    </div>
                    <div className="text-sm text-[#2B3F4D] mt-0.5 truncate">
                        {isAdmin && <>{incident.customer_name} · </>}
                        {fmtDt(incident.created_at, lang)}
                    </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                    {(incident.messages || []).length > 0 && (
                        <span className="inline-flex items-center gap-1">
                            <MessageSquare className="w-3.5 h-3.5" />
                            {incident.messages.length}
                        </span>
                    )}
                    {(incident.attachments || []).length > 0 && (
                        <span className="inline-flex items-center gap-1">
                            <Paperclip className="w-3.5 h-3.5" />
                            {incident.attachments.length}
                        </span>
                    )}
                    <ArrowRight className="w-4 h-4 text-[#A57F39]" />
                </div>
            </div>
        </Card>
    );
}

function CreateIncidentDialog({ open, onOpenChange, onCreated }) {
    const nav = useNavigate();
    const [customerQuery, setCustomerQuery] = useState("");
    const [customers, setCustomers] = useState([]);
    const [form, setForm] = useState({
        customer_user_id: "",
        booking_id: "",
        boat_id: "",
        title: "",
        description: "",
        severity: "moderate",
    });
    const [saving, setSaving] = useState(false);
    const [boats, setBoats] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
        if (!open) return;
        setForm({ customer_user_id: "", booking_id: "", boat_id: "", title: "", description: "", severity: "moderate" });
        setSelectedCustomer(null);
        setCustomerQuery("");
        api.get("/boats").then((r) => setBoats(r.data || []));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (selectedCustomer) return; // don't fetch while a customer is already picked
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/incidents-customers${customerQuery ? `?q=${encodeURIComponent(customerQuery)}` : ""}`);
                setCustomers(data || []);
            } catch { /* noop */ }
        }, 250);
        return () => clearTimeout(t);
    }, [customerQuery, open, selectedCustomer]);

    useEffect(() => {
        if (!form.customer_user_id) { setBookings([]); return; }
        api.get(`/bookings?customer_user_id=${form.customer_user_id}`)
            .then((r) => setBookings(r.data || []))
            .catch(() => setBookings([]));
    }, [form.customer_user_id]);

    const create = async () => {
        if (!form.customer_user_id || !form.title.trim()) {
            toast.error("Velg kunde og skriv en tittel");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.booking_id) delete payload.booking_id;
            if (!payload.boat_id) delete payload.boat_id;
            const { data } = await api.post("/incidents", payload);
            toast.success(`Sak ${data.reference_no} opprettet`);
            onCreated?.();
            nav(`/incidents/${data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke opprette");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg max-h-[85vh] overflow-y-auto"
                data-testid="incidents-create-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Ny skadesak</DialogTitle>
                    <DialogDescription>
                        Registrer en hendelse. Kunden vil se saken i sin portal med en gang den opprettes.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Kunde *</Label>
                        {selectedCustomer ? (
                            <div
                                className="mt-1 flex items-center justify-between gap-2 rounded-md border border-[#BD984D] bg-[#BD984D]/10 px-3 py-2"
                                data-testid="incidents-create-customer-selected"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <UserCheck className="w-4 h-4 text-[#A57F39] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="font-semibold text-[#0C1824] truncate">
                                            {selectedCustomer.name}
                                        </div>
                                        <div className="text-xs text-[#2B3F4D] truncate">
                                            {selectedCustomer.email}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedCustomer(null);
                                        setForm({ ...form, customer_user_id: "", booking_id: "" });
                                        setCustomerQuery("");
                                    }}
                                    className="text-[#A57F39] hover:bg-[#BD984D]/20 shrink-0"
                                    data-testid="incidents-create-customer-clear"
                                >
                                    <XIcon className="w-4 h-4 mr-1" /> Endre
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Input
                                    value={customerQuery}
                                    onChange={(e) => setCustomerQuery(e.target.value)}
                                    placeholder="Søk etter navn eller e-post…"
                                    data-testid="incidents-create-customer-search-input"
                                    autoFocus
                                />
                                <div className="mt-1 max-h-40 overflow-y-auto rounded border border-slate-200 text-sm">
                                    {customers.map((c) => (
                                        <button
                                            type="button"
                                            key={c.id}
                                            onClick={() => {
                                                setForm({ ...form, customer_user_id: c.id });
                                                setSelectedCustomer(c);
                                            }}
                                            className="w-full text-left px-2 py-1.5 hover:bg-slate-50"
                                            data-testid={`incidents-create-customer-btn-${c.id}`}
                                        >
                                            {c.name} <span className="text-slate-500">— {c.email}</span>
                                        </button>
                                    ))}
                                    {customers.length === 0 && (
                                        <div className="px-2 py-1.5 text-slate-500 italic">Ingen treff</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {bookings.length > 0 && (
                        <div>
                            <Label>Knytt til booking (valgfritt)</Label>
                            <Select
                                value={form.booking_id || "_none"}
                                onValueChange={(v) => setForm({ ...form, booking_id: v === "_none" ? "" : v })}
                            >
                                <SelectTrigger><SelectValue placeholder="Ingen" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">Ingen</SelectItem>
                                    {bookings.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            {b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div>
                        <Label>Båt (valgfritt)</Label>
                        <Select
                            value={form.boat_id || "_none"}
                            onValueChange={(v) => setForm({ ...form, boat_id: v === "_none" ? "" : v })}
                        >
                            <SelectTrigger><SelectValue placeholder="Ingen" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_none">Ingen</SelectItem>
                                {boats.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label>Tittel *</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="F.eks. Kollisjon i sluse 3"
                            maxLength={140}
                            data-testid="incidents-create-title"
                        />
                    </div>

                    <div>
                        <Label>Beskrivelse</Label>
                        <Textarea
                            rows={4}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Hva skjedde? Vitner, tid, sted, skadeomfang…"
                            data-testid="incidents-create-description"
                        />
                    </div>

                    <div>
                        <Label>Alvorlighet</Label>
                        <Select
                            value={form.severity}
                            onValueChange={(v) => setForm({ ...form, severity: v })}
                        >
                            <SelectTrigger data-testid="incidents-create-severity">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.keys(SEVERITY_META).map((k) => (
                                    <SelectItem key={k} value={k}>{SEVERITY_META[k].no}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
                    <Button
                        onClick={create}
                        disabled={saving || !form.customer_user_id || !form.title.trim()}
                        className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                        data-testid="incidents-create-submit"
                    >
                        {saving ? "Oppretter…" : "Opprett sak"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
